/**
 * personModel.ts
 * Pure domain logic for the "Person ↔ Vorgang" contact Kanban.
 */
import type { Task, FullProject } from '../../types';
import { isSystemProject, isCommProject, areProjectIdsMatching } from '../../utils/idNormalizer';

export type CommColumnId = 'pool' | 'open' | 'pending' | 'completed' | 'archived';

export type CommTask = Task & { projectName?: string; projectColor?: string };

/** Statuses an old-style contact record may carry when it was used as a "Vorgang" itself. */
const LEGACY_ACTIVE_STATUSES = new Set([
  'open',
  'todo',
  'inactive',
  'in_progress',
  'pending',
  'contacted',
  'hold',
  'completed',
]);

const TITLE_TOKENS = new Set([
  'dr',
  'drin',
  'dr.in',
  'ddr',
  'mag',
  'maga',
  'mag.a',
  'mmag',
  'di',
  'dipl',
  'ing',
  'dipl-ing',
  'prof',
  'univ',
  'univ-prof',
  'msc',
  'bsc',
  'ma',
  'ba',
  'mba',
  'phd',
  'med',
  'rer',
  'nat',
  'techn',
  'dkfm',
  'bed',
  'mas',
  'llm',
  'hr',
  'ing.',
  'ök',
  'ökr',
]);

/** Normalizes a person name for identity matching (case, academic titles, whitespace, punctuation). */
export function normalizePersonName(name?: string | null): string {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[(),]/g, ' ')
    .split(/\s+/)
    .map((tok) => tok.trim())
    .filter(Boolean)
    .filter((tok) => {
      const bare = tok.replace(/\./g, '');
      return (
        !TITLE_TOKENS.has(tok) &&
        !TITLE_TOKENS.has(bare) &&
        !TITLE_TOKENS.has(tok.replace(/\.$/, ''))
      );
    })
    .map((tok) => tok.replace(/\.$/, ''))
    .join(' ')
    .trim();
}

export function normalizeEmail(email?: string | null): string {
  return (email || '').trim().toLowerCase();
}

/**
 * Extracts normalized candidate person names from a raw stakeholder or assignee string.
 */
export function extractNormalizedPersonCandidates(raw?: string | null): string[] {
  if (!raw) return [];
  const results = new Set<string>();
  const full = normalizePersonName(raw);
  if (full && full !== 'all') results.add(full);

  const parenRegex = /\(([^)]+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = parenRegex.exec(raw)) !== null) {
    const inner = normalizePersonName(match[1]);
    if (inner && inner !== 'all' && inner !== 'zielgruppe') results.add(inner);
  }

  if (raw.includes('(')) {
    const outer = normalizePersonName(raw.replace(/\([^)]*\)/g, ' '));
    if (outer && outer !== 'all') results.add(outer);
  }

  return Array.from(results);
}

/** Stable identity key of a person: e-mail if present, otherwise normalized name. */
export function computePersonKey(p: {
  contactEmail?: string;
  name?: string;
  assignee?: string;
}): string {
  const mail = normalizeEmail(p.contactEmail);
  if (mail) return `e:${mail}`;
  const n = normalizePersonName(p.name || p.assignee);
  return n ? `n:${n}` : '';
}

export function isPersonRecord(t: Task): boolean {
  return (
    t.type === 'contact' ||
    t.status === 'pool' ||
    (typeof t.id === 'string' && t.id.startsWith('pool_contact_'))
  );
}

/**
 * A pure person contact is master data living in the address book pool (or dateless/actionless contact).
 */
export function isPurePersonContact(t: Task): boolean {
  if (
    t.status === 'pool' ||
    (typeof t.id === 'string' && t.id.startsWith('pool_contact_'))
  )
    return true;
  if (t.type === 'contact') {
    if (isLegacyActiveContact(t) || Boolean(t.start?.trim() || t.end?.trim())) return false;
    return true;
  }
  return false;
}

/** Old data model: a contact record that itself sits in an action column (pre-migration). */
export function isLegacyActiveContact(t: Task): boolean {
  return t.type === 'contact' && !!t.status && LEGACY_ACTIVE_STATUSES.has(t.status);
}

/**
 * Returns true if the task represents a Contact-Vorgang (contact-linked process).
 */
export function isContactVorgang(t: Task, isFromCommProject = false): boolean {
  if (isPurePersonContact(t)) return false;
  return (
    isFromCommProject ||
    t.category === 'Kommunikation' ||
    Boolean(t.contactId) ||
    Boolean(t.personKey) ||
    (t.type === 'contact' && isLegacyActiveContact(t))
  );
}

/**
 * Returns a clean display name for a task or Contact-Vorgang.
 */
export function getContactVorgangDisplayName(t: Task, isFromCommProject = false): string {
  const trimmed = (t.name || '').trim();
  if (trimmed) return trimmed;
  if (isContactVorgang(t, isFromCommProject)) {
    return t.assignee?.trim() ? `Vorgang mit ${t.assignee.trim()}` : 'Kontakt-Vorgang';
  }
  return '';
}

/**
 * Resolves the effective real project for a task or Contact-Vorgang stored in `comm_internal`.
 */
export function resolveEffectiveProjectForTask(
  t: Task,
  sourceProj: FullProject,
  allProjects: FullProject[]
): FullProject {
  if (!isCommProject(sourceProj.id) && !isSystemProject(sourceProj.id, sourceProj.name)) {
    return sourceProj;
  }

  const targetProjId =
    t.projectId && !isSystemProject(t.projectId)
      ? t.projectId
      : Array.isArray(t.projectIds)
        ? t.projectIds.find((pid) => pid && !isSystemProject(pid))
        : undefined;

  if (targetProjId) {
    const matched = allProjects.find((rp) => areProjectIdsMatching(rp.id, targetProjId));
    if (matched) return matched;
  }

  const realProjects = allProjects.filter(
    (rp) => !rp.archived && !isSystemProject(rp.id, rp.name)
  );
  if (t.contactId) {
    const cid = String(t.contactId);
    const byContact = realProjects.find((rp) =>
      (rp.tasks || []).some((rt) => String(rt.id) === cid || String(rt.contactId) === cid)
    );
    if (byContact) return byContact;
  }

  const normAssignee = normalizePersonName(t.assignee);
  if (normAssignee) {
    const byAssignee = realProjects.find(
      (rp) =>
        (rp.tasks || []).some(
          (rt) =>
            normalizePersonName(rt.assignee) === normAssignee ||
            (rt.type === 'contact' && normalizePersonName(rt.name) === normAssignee)
        ) ||
        (rp.metadata?.stakeholders || []).some((s: any) =>
          extractNormalizedPersonCandidates(s?.name).includes(normAssignee)
        )
    );
    if (byAssignee) return byAssignee;
  }

  return sourceProj;
}

export function isVorgang(t: Task): boolean {
  return t.type !== 'contact' && t.type !== 'milestone' && t.type !== 'deadline';
}

/** Maps a Vorgang (or legacy contact) to its Kanban column. */
export function getVorgangColumn(t: Task): CommColumnId | null {
  if (t.archived) return 'archived';
  const s = t.status || (t.completed ? 'completed' : 'open');
  if (s === 'pool') return null;
  if (t.completed || s === 'completed' || s === 'done') return 'completed';
  if (s === 'pending' || s === 'contacted' || s === 'hold') return 'pending';
  return 'open';
}

export const getPersonDisplayName = (t: Task): string =>
  (t.type === 'contact' ? t.name || t.assignee || '' : t.assignee || t.name || '').trim();

export function getInitials(name?: string): string {
  const parts = normalizePersonName(name)
    .split(' ')
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic hue for an organization (same algorithm as the contact network graph). */
export function getOrgHue(org?: string, fallbackSeed?: string): number {
  const s = (org || fallbackSeed || '').trim();
  if (!s) return 234;
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return Math.abs(hash) % 360;
}

export function getAvatarGradient(org?: string, name?: string): string {
  const hue = getOrgHue(org, name);
  return `linear-gradient(135deg, hsl(${hue}, 72%, 58%), hsl(${(hue + 32) % 360}, 70%, 46%))`;
}

export {
  type PersonEntry,
  type SyntheticPersonSeed,
  resolveStakeholderSeed,
  buildPersons,
} from './personDeduplicationBuilder';

export {
  toISODate,
  daysBetween,
  addDaysISO,
  getDeadlinePresets,
  formatShortDate,
  type DeadlineTone,
  type DeadlineInfo,
  getDeadlineInfo,
  getDeadlineLabelForColumn,
  daysSinceContact,
  formatLastContact,
  compareByDeadline,
  compareBySortOrder,
  type ColumnItem,
  stackDndId,
  vorgangDndId,
  personDndId,
  avatarDndId,
  groupColumnItems,
  sortOrderBetween,
  isRecentlyCompleted,
} from './personDateAndStackUtils';

export {
  planLegacyContactMigration,
  type MigrationPreviewItem,
  type MigrationPlan,
} from './personLegacyMigration';

export {
  extractProjectPersons,
  type ExtractProjectPersonsParams,
  type SidebarPersonInfo,
  type ProjectPeopleResult,
} from './extractProjectPersons';

export { healCommProjectAssignments } from './personHealing';
